// 材料上传：后端签发 → 有 direct 时直传 R2（PUT + 回调 complete），直传失败或无 direct 时退回后端代传
import { api, getToken } from "./api";
import { apiUrl } from "./net";

export type ProgressFn = (loaded: number, total: number) => void;

/** XHR PUT：fetch 拿不到上传进度，这里用 XMLHttpRequest */
export function xhrPut(url: string, body: Blob, headers: Record<string, string>, onProgress?: ProgressFn): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    for (const [k, v] of Object.entries(headers)) {
      // 浏览器禁止手动设置的头（如 content-length）跳过
      if (/^(content-length|host)$/i.test(k)) continue;
      xhr.setRequestHeader(k, v);
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded, e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else {
        let msg = `上传失败（${xhr.status}）`;
        try {
          const j = JSON.parse(xhr.responseText) as { message?: string };
          if (j.message) msg = j.message;
        } catch {
          /* 非 JSON */
        }
        reject(new Error(msg));
      }
    };
    xhr.onerror = () => reject(new Error("上传网络错误"));
    xhr.send(body);
  });
}

/** 文件夹上传时保留相对路径：「/」换成全角「／」，后端按文件名存，层级仍看得出来 */
export function uploadName(file: File): string {
  const name = file.webkitRelativePath ? file.webkitRelativePath.replace(/\//g, "／") : file.name;
  // 后端文件名上限 200：路径太深时保留末尾（文件名本身最重要）
  return name.length > 190 ? `…${name.slice(-189)}` : name;
}

/** 文件夹里的系统 / 临时文件不上传 */
export function isJunkFile(file: File): boolean {
  return /^(\.|~\$)|^(Thumbs\.db|desktop\.ini)$/i.test(file.name);
}

export async function uploadTaskFile(taskId: string, key: string, file: File, onProgress?: ProgressFn): Promise<{ via: "direct" | "proxy"; fileId: string }> {
  const contentType = file.type || "application/octet-stream";
  const ticket = await api.requestUpload(taskId, { key, name: uploadName(file), contentType, size: file.size });
  if (ticket.direct) {
    try {
      await xhrPut(ticket.direct.url, file, ticket.direct.headers, onProgress);
      await api.completeUpload(ticket.fileId);
      return { via: "direct", fileId: ticket.fileId };
    } catch (e) {
      // 直传失败（CORS / 网络）→ 后端代传兜底
      console.warn("[upload] 直传失败，改走后端代传", e);
    }
  }
  const token = getToken();
  await xhrPut(
    apiUrl(ticket.proxyUrl),
    file,
    { "Content-Type": "application/octet-stream", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    onProgress,
  );
  return { via: "proxy", fileId: ticket.fileId };
}
